import API_BASE from "config/api";
import { clearAuthState, getAccessToken, getRefreshToken, setTokens } from "utils/auth";

/**
 * Patch window.fetch to transparently refresh expired access tokens once.
 * Applies to any request that returns 401 or a body with token-related auth detail.
 */
export function installAuthFetchInterceptor() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  if (window.__dfAuthFetchPatched) return; // idempotent guard

  const originalFetch = window.fetch.bind(window);
  let refreshPromise = null;
  let redirectingToLogin = false;

  const toHeaderObject = (headers = {}) => {
    try {
      const h = new Headers(headers);
      const obj = {};
      h.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    } catch {
      return {};
    }
  };

  const refreshTokens = async () => {
    if (refreshPromise) return refreshPromise;
    const refresh = getRefreshToken();
    if (!refresh) return null;
    refreshPromise = (async () => {
      try {
        const res = await originalFetch(`${API_BASE}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refresh }),
        });
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        const newAccess = data?.access_token;
        const newRefresh = data?.refresh_token || refresh;
        if (!newAccess) return null;
        setTokens(newAccess, newRefresh);
        return { access: newAccess, refresh: newRefresh };
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  };

  const isAuthEndpoint = (urlValue) => {
    try {
      const raw = typeof urlValue === "string" ? urlValue : urlValue?.url || "";
      return /\/api\/auth\/(login|refresh|register|logout)\/?$/.test(raw);
    } catch {
      return false;
    }
  };

  const redirectToLogin = () => {
    if (redirectingToLogin) return;
    redirectingToLogin = true;
    clearAuthState();
    try {
      const path = window.location?.pathname || "";
      if (path !== "/login") {
        window.location.replace("/login");
      }
    } catch {
      /* ignore redirect errors */
    } finally {
      setTimeout(() => {
        redirectingToLogin = false;
      }, 1000);
    }
  };

  const shouldRetry = async (response) => {
    if (response.status === 401) return true;
    try {
      const clone = response.clone();
      const data = await clone.json();
      const detail = (data?.detail || "").toString().toLowerCase();
      if (detail.includes("access token expired")) return true;
      if (detail.includes("invalid access token")) return true;
      if (detail.includes("token expired")) return true;
    } catch {
      /* ignore parse errors */
    }
    return false;
  };

  window.fetch = async (input, init = {}) => {
    const resp = await originalFetch(input, init);
    const retry = await shouldRetry(resp);
    if (!retry) return resp;

    const tokens = await refreshTokens();
    if (!tokens?.access) {
      if (!isAuthEndpoint(input) && getAccessToken()) {
        redirectToLogin();
      }
      return resp;
    }

    const headers = {
      ...toHeaderObject(init.headers),
      Authorization: `Bearer ${tokens.access}`,
    };
    const retryInit = { ...init, headers };
    return originalFetch(input, retryInit);
  };

  window.__dfAuthFetchPatched = true;
}

export default installAuthFetchInterceptor;
